import {
  Theme,
  type ContainersFragment,
  type GetPagesQuery,
  type Locale,
  type SectionFormFragment,
} from '@seek/cmsu-cms-connect';
import type { Country, Language, SiteName } from '@seek/melways-sites';
import {
  Box,
  ContentBlock,
  Divider,
  HiddenVisually,
  PageBlock,
} from 'braid-design-system';
import React from 'react';

import { CMSUProvider } from '../../hooks/useCMSUContext';
import {
  BannerRenderer,
  BlockRenderer,
  type BannerRenderers,
  type BlockItemRenderers,
} from '../../renderer';
import { universal } from '../../renderer/Banner/renderers';
import { BoxWithDecorator } from '../../shared/decorations';
import {
  getBlockBorderProps,
  getBoxBackgroundProps,
  hasDivider,
} from '../../shared/helper';
import { BoxDecoratorGroup } from '../BoxDecoratorGroup/BoxDecoratorGroup';
import { Breadcrumbs } from '../Breadcrumbs/Breadcrumbs';
import { getBreadcrumbItems } from '../Breadcrumbs/breadcrumbHelper';
import { PromotionalBanner } from '../PromotionalBanner/PromotionalBanner';
import { SectionForm } from '../SectionForm/SectionForm';
import { SectionMenu } from '../SectionMenu/SectionMenu';
import { SubscriptionForm } from '../SubscriptionForm/SubscriptionForm';
import { Tiles, getTileProps } from '../Tiles/Tiles';

export const PageComponent = ({
  bannerRenderers = universal,
  country,
  enabledSubscriptionForm,
  fullURL,
  language,
  melwaysLocale,
  page,
  renderers,
  subscriptionFormSourceName,
  wptSalesForceApi,
  utmParameters,
  sourceName,
  privacyUrl,
  recaptchaKey,
  site,
}: {
  bannerRenderers?: BannerRenderers;
  country: Country;
  enabledSubscriptionForm?: boolean;
  fullURL: string;
  language: Language;
  melwaysLocale: string;
  page: GetPagesQuery['pages'][0];
  renderers: BlockItemRenderers;
  subscriptionFormPrivacyUrl: string;
  subscriptionFormSourceName: string;
  wptSalesForceApi: string;
  utmParameters?: string;
  sourceName: string;
  privacyUrl: string;
  recaptchaKey: string;
  site: SiteName;
}) => {
  const {
    title,
    promotionalBanner,
    banner,
    containers,
    relatedPageSection,
    enableBreadcrumbs,
  } = page;
  const {
    headerOptions,
    parentSection,
    sectionName,
    displayName: sectionDisplayName,
    theme,
  } = relatedPageSection || {};
  const subNavLinks =
    headerOptions?.headerType?.__typename === 'SeekHeader' &&
    headerOptions.headerType?.subnavLinks;

  const items = getBreadcrumbItems({
    locale: melwaysLocale as Locale,
    parentSection: parentSection?.sectionName || '',
    section: sectionName || '',
    title,
    sectionDisplayName,
    parentSectionDisplayName: parentSection?.displayName,
  });

  return (
    <CMSUProvider
      config={{
        language,
        country,
        theme: theme || Theme.SeekJobs,
        utmParameters,
        site,
      }}
    >
      {subNavLinks ? (
        <SectionMenu currentPage={title} links={subNavLinks} />
      ) : null}
      <Box background="surface">
        <HiddenVisually>
          <h1>{title}</h1>
        </HiddenVisually>
        {/* Promotional Banner */}
        {promotionalBanner && <PromotionalBanner {...promotionalBanner} />}
        {/* Banner */}
        {banner && (
          <BannerRenderer
            renderers={bannerRenderers}
            banner={banner}
            country={country}
          />
        )}
        {enableBreadcrumbs && (
          <PageBlock width="large">
            <Box paddingTop={!banner ? 'gutter' : undefined}>
              <Breadcrumbs items={items} />
            </Box>
          </PageBlock>
        )}
        {/* Containers */}
        {(containers as ContainersFragment[]).map(
          ({ id, box, htmlId, tile, blocks }, containerIdx) => (
            <React.Fragment key={id}>
              <BoxWithDecorator
                customDecoration={box?.customDecoration}
                textAlign={box?.textAlign || 'left'}
                paddingX={{
                  mobile: 'none',
                  tablet: 'none',
                  desktop: box?.paddingWidth || 'medium',
                }}
                className="sk-container"
                paddingY={box?.paddingHeight || 'xlarge'}
                id={htmlId || undefined}
                {...getBoxBackgroundProps(box?.background)}
              >
                <ContentBlock width={box?.width || 'medium'}>
                  <Box
                    {...getBlockBorderProps(box?.blockBorder)}
                    borderRadius={{
                      mobile: 'none',
                      tablet: box?.borderRadius || undefined,
                    }}
                    paddingX={box?.blockBorder ? 'gutter' : 'none'}
                    paddingY={box?.blockBorder ? 'medium' : 'none'}
                    position="relative"
                  >
                    <BoxDecoratorGroup decorators={box?.decorationAsset} />
                    <Tiles {...getTileProps(tile)}>
                      {/* Blocks */}
                      {blocks.map((block) => (
                        <BlockRenderer
                          key={block.id}
                          block={block}
                          renderers={renderers}
                          additionalData={{
                            page,
                            fullURL,
                          }}
                          containerBox={{
                            paddingHeight: box?.paddingHeight,
                            textAlign: box?.textAlign,
                          }}
                          containerIdx={containerIdx}
                        />
                      ))}
                    </Tiles>
                  </Box>
                </ContentBlock>
              </BoxWithDecorator>

              {/* Divider */}
              {hasDivider(containers, containerIdx) && (
                <ContentBlock width="medium">
                  <Box textAlign="left" style={{ margin: 'auto' }}>
                    <Divider />
                  </Box>
                </ContentBlock>
              )}
            </React.Fragment>
          ),
        )}
        {enabledSubscriptionForm && (
          <SectionForm
            sectionName={sectionName}
            sectionForm={relatedPageSection?.sectionForm as SectionFormFragment}
            melwaysLocale={melwaysLocale}
            sourceName={sourceName}
            country={country}
            privacyUrl={privacyUrl}
            recaptchaKey={recaptchaKey}
            site={site}
            wptSalesForceApi={wptSalesForceApi}
          />
        )}
        {enabledSubscriptionForm && (
          <Box
            marginTop={{
              mobile: 'gutter',
              tablet: 'xlarge',
              desktop: 'xxlarge',
            }}
            paddingBottom={{
              mobile: 'gutter',
              tablet: 'xlarge',
              desktop: 'xxlarge',
            }}
          >
            <SubscriptionForm
              type={sectionName!}
              locale={melwaysLocale}
              country={country}
              sourceName={subscriptionFormSourceName}
              wptSalesForceApi={wptSalesForceApi}
            />
          </Box>
        )}
      </Box>
    </CMSUProvider>
  );
};
